// `useVisitedPages()` — FTU completion tracker (US-056).
//
// DS-GAP-DESIGN (US-056 + coordination with 5a.1):
//   The user-story asks us to PATCH `/me` to persist the visited-pages set.
//   At the time of writing (Wave 5a.2), the `users.visitedPages` column does
//   NOT exist in `packages/db/src/schema/auth.ts`, AND `useUpdateSettings()` is
//   still a Wave-5 stub that throws. Adding the column from this worktree
//   would step on Agent 5a.1's schema/`/me` PATCH route work — both agents
//   working the same migration is the recipe for a merge conflict in waves.md.
//
//   So this hook ships **localStorage-only persistence** for Wave 5a, with a
//   clearly-typed contract that 5a.1 (or Wave 7's settings work) can swap in
//   the PATCH /me round-trip without touching call sites:
//     - `record(page)` flips the in-memory set + persists to localStorage.
//     - `has(page)` returns whether the page has ever been visited.
//     - `visited` exposes the immutable set for FTU checklist read-through.
//
//   When the column lands, `record()` should additionally fire
//   `useUpdateSettings({ visitedPages })` and reconcile on `useMe()` refetch.
//
// The hook is consumed by `<AppShell>` once on mount (it auto-records the
// current pathname) and by FTU checklist consumers via `has()`.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRouterState } from '@tanstack/react-router';

const STORAGE_KEY = 'barel:ftu:visitedPages';

/**
 * Map a pathname to a stable "page id" the FTU taxonomy understands. The set
 * mirrors the prototype — `editvoice` → `voice`, `businesses`/`editbusiness`
 * → `business`, `campaigns` → `campaign`. Unknown paths are tracked verbatim.
 */
export function pathnameToPageId(pathname: string): string {
  if (pathname.startsWith('/accounts/') && pathname.includes('/voice')) return 'voice';
  if (pathname.startsWith('/accounts')) return 'accounts';
  if (pathname.startsWith('/businesses')) return 'business';
  if (pathname.startsWith('/campaigns')) return 'campaign';
  if (pathname.startsWith('/inbox')) return 'inbox';
  if (pathname.startsWith('/leads')) return 'leads';
  if (pathname.startsWith('/relances')) return 'relances';
  if (pathname.startsWith('/settings')) return 'settings';
  return pathname.replace(/^\//, '') || 'index';
}

function readStored(): readonly string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((v): v is string => typeof v === 'string')) {
      return parsed;
    }
    return [];
  } catch {
    return [];
  }
}

function writeStored(pages: readonly string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...pages]));
  } catch {
    /* noop — quota / private mode */
  }
}

export interface UseVisitedPagesReturn {
  /** Immutable view of the visited-page id set. */
  visited: readonly string[];
  /** True when the page id has ever been recorded. */
  has: (pageId: string) => boolean;
  /** Record a page id (idempotent). */
  record: (pageId: string) => void;
}

/**
 * Subscribe to the visited-pages set + record route changes automatically.
 * Mount once at the shell level — feature pages don't need to call this
 * explicitly; the router state subscription captures every navigation.
 */
export function useVisitedPages(): UseVisitedPagesReturn {
  const [visited, setVisited] = useState<readonly string[]>(() => readStored());
  const visitedSetRef = useRef<Set<string>>(new Set(visited));

  // Keep the ref in lock-step with the state so `record` checks O(1).
  useEffect(() => {
    visitedSetRef.current = new Set(visited);
  }, [visited]);

  const record = useCallback((pageId: string): void => {
    if (!pageId) return;
    if (visitedSetRef.current.has(pageId)) return;
    visitedSetRef.current.add(pageId);
    setVisited((prev) => {
      if (prev.includes(pageId)) return prev;
      const next = [...prev, pageId];
      writeStored(next);
      return next;
    });
    // TODO Wave 7 (or 5a.1) — once `users.visitedPages` lands + the PATCH /me
    // route exists, plumb `useUpdateSettings({ visitedPages: nextArray })`
    // here. Keep the localStorage write as the optimistic path.
  }, []);

  const routerState = useRouterState();
  const pathname = routerState.location.pathname;
  useEffect(() => {
    if (!pathname) return;
    record(pathnameToPageId(pathname));
  }, [pathname, record]);

  const has = useCallback((pageId: string): boolean => visitedSetRef.current.has(pageId), []);

  return useMemo<UseVisitedPagesReturn>(() => ({ visited, has, record }), [visited, has, record]);
}
